import gsap from 'gsap';

import { BaseBackgroundDrawer } from './BaseBackgroundDrawer';
import { gameConfig } from '../../../core/configs';
import {
  backgroundAnimationNames,
  backgroundResource,
  backgroundResourceNames,
  bonusBackgroundResourceName,
  bonusBackgroundResources,
  preBonusBackgroundResourceName,
  preBonusBackgroundResources,
} from '../../../core/gameConstants';
import {
  LAST_RESULTS,
  LAST_RESULTS_BONUS,
} from '../../../stateManagment/gameState';
import { wheelDrawer } from '../../wheel/WheelDrawer';

export class BackgroundDesktopDrawer extends BaseBackgroundDrawer {
  constructor() {
    super();
    this.resources = null;
    this.mainResource = null;
    this.preBonusresources = null;
    this.animations = {};
    this.container = null;
    this.loader = this.createLoader();
    this.preBonusLoader = this.createLoader();
    this.bonusLoader = this.createLoader();
  }

  setBackgroundFirstLayer() {
    const bg = this.createSprite(this.resources[backgroundResourceNames.backgroundFirstLayer]);
    this.container.addChild(bg);
  }

  setBackgroundSecondLayer() {
    const bg = this.createSprite(this.resources[backgroundResourceNames.backgroundSecondLayer]);
    this.container.addChild(bg);
  }

  setStoneAnimations() {
    const {
      [backgroundResourceNames.stones]: stones,
    } = this.resources;

    const stonesTexture = this.createSpine(stones);

    this.setPosition(stonesTexture, gameConfig.backgroundAnimationPosition);

    this.__setAnimations(stonesTexture);
    stonesTexture.state.timeScale = 0.5;
    this.animations.glow();
    this.container.addChild(stonesTexture);
    return this;
  }

  setTopPlanetsAnimation() {
    const {
      [backgroundResourceNames.topPlants]: topPlants,
    } = this.resources;
    const topPlantsTexture = this.createSpine(topPlants);
    this.setPosition(topPlantsTexture, gameConfig.backgroundAnimationPosition);
    topPlantsTexture.state.setAnimation(0, 'animation', true);
    this.container.addChild(topPlantsTexture);
    return this;
  }

  setPlanetsAnimation() {
    const {
      [backgroundResourceNames.plants]: plants,
    } = this.resources;
    const plantsTexture = this.createSpine(plants);
    this.setPosition(plantsTexture, gameConfig.backgroundAnimationPosition);
    plantsTexture.state.setAnimation(0, 'animation', true);
    this.container.addChild(plantsTexture);
    return this;
  }

  setCoinsAnimations() {
    const {
      [backgroundResourceNames.coins]: coins,
    } = this.resources;
    const coinsTexture = this.createSpine(coins);
    this.setPosition(coinsTexture, gameConfig.backgroundAnimationPosition);
    coinsTexture.state.setAnimation(0, 'animation', true);
    this.container.addChild(coinsTexture);
  }

  setLastResult({ lastResultObserver = LAST_RESULTS } = {}) {
    const lastResult = this.getLastResultUi({
      scale: 1.2,
      crystalW: 36,
      crystalScale: .18,
      parentWidth: gameConfig.width,
      fullRectWidth: 278,
      lastResultObserver,
      calcFirstElementPosition: (...args) => this.__calcEndAnimation(...args),
    });
    this.container.addChild(lastResult);
  }

  setUserIdDeposit() {
    const container = this.getUserIdAndDeposit({
      icon: this.createSprite(this.resources[backgroundResourceNames.plusIcon]),
      // freeBetIcon: this.createSprite(this.resources[backgroundResourceNames.freeBetButtonIcon]),
    });
    this.setPosition(container, { x: gameConfig.width - 40 - container.size, y: 40 });
    this.container.addChild(container);
  }

  moveCrystal(crystalName, [wheel, container] = wheelDrawer.containerAndWheel) {
    return new Promise(resolve => {
      const content = this.createContainer('deleteElement');
      const [star, starAnimation] = this.__createStar();
      const crystal = this.createSprite(this.resources[crystalName]);
      starAnimation();
      crystal.anchor.set(.5);
      crystal.scale.set(.2);
      content.addChild(
        star,
        crystal,
      );
      this.__calcStartAnimation({ wheel });
      this.setPosition(content, this.startPosition);
      container.addChild(content);
      gsap.to(
        crystal,
        {
          duration: .4,
          pixi: {
            scale: 1,
          },
        },
      );
      gsap.to(
        crystal,
        {
          delay: .4,
          duration: .5,
          pixi: {
            scale: .2,
          },
        },
      );
      gsap.to(
        content,
        {
          delay: .4,
          duration: .5,
          ...this.endPosition,
          onComplete: () => {
            this.lastCrystal.starAnimation();
            this.lastCrystal.crystal.alpha = 1;
            container.removeChildAt(container.children.length - 1);
            resolve();
          },
        },
      );
    })
  }

  __setAnimations(texture) {
    this.animations = {
      blue: (loop = false) => texture.state.setAnimation(0, backgroundAnimationNames.blue, loop),
      glow: (loop = false) => texture.state.setAnimation(0, backgroundAnimationNames.glow, loop),
      green: (loop = false) => texture.state.setAnimation(0, backgroundAnimationNames.green, loop),
      magenta: (loop = false) => texture.state.setAnimation(0, backgroundAnimationNames.magenta, loop),
      yellow: (loop = false) => texture.state.setAnimation(0, backgroundAnimationNames.yellow, loop),
      gold: (loop = false) => texture.state.setAnimation(0, backgroundAnimationNames.yellow, loop),
    };
    texture.state.timeScale = 0.5;
    this.animations.glow();
  }

  __calcEndAnimation({ x, y }) {
    if (!this.endPosition) {
      const { x: wx, y: wy } = gameConfig.wheelPosition;
      this.endPosition = {
        x: x - wx,
        y: y - wy,
      };
    }
  }

  setPreBonusBackgroundFirstLayer() {
    const bg = this.createSprite(this.resources[preBonusBackgroundResourceName.backgroundFirstLayer]);
    this.container.addChild(bg);
  }

  setPreBonusBackgroundSecondLayer() {
    const bg = this.createSprite(this.resources[preBonusBackgroundResourceName.backgroundSecondLayer]);
    this.container.addChild(bg);
  }

  setPreBonusMovingLiana() {
    const texture = this.createSpine(this.resources[preBonusBackgroundResourceName.preBonusMovingLiana]);
    this.setPosition(texture, gameConfig.backgroundAnimationPosition);
    texture.state.setAnimation(0, 'animation', true);
    this.container.addChild(texture);
  }

  setPreBonusCoins() {
    const texture = this.createSpine(this.resources[preBonusBackgroundResourceName.preBonusCoins]);
    this.setPosition(texture, gameConfig.backgroundAnimationPosition);
    texture.state.setAnimation(0, 'animation', true);
    this.container.addChild(texture);
  }

  setPreBonusPlants() {
    const texture = this.createSpine(this.resources[preBonusBackgroundResourceName.preBonusPlants]);
    this.setPosition(texture, gameConfig.backgroundAnimationPosition);
    texture.state.setAnimation(0, 'animation', true);
    this.container.addChild(texture);
  }

  setPreBonusStones() {
    const texture = this.createSpine(this.resources[preBonusBackgroundResourceName.preBonusStones]);
    this.setPosition(texture, gameConfig.backgroundAnimationPosition);
    this.__setAnimations(texture);
    texture.state.timeScale = 0.5;
    this.animations.glow(true);
    this.container.addChild(texture);
  }

  setPreBonusTopPlants() {
    const texture = this.createSpine(this.resources[preBonusBackgroundResourceName.preBonusTopPlants]);
    this.setPosition(texture, gameConfig.backgroundAnimationPosition);
    texture.state.setAnimation(0, 'animation', true);
    this.container.addChild(texture);
  }

  setBonusBackgroundFirstLayer() {
    const bg = this.createSprite(this.resources[bonusBackgroundResourceName.backgroundFirstLayer]);
    this.container.addChild(bg);
  }

  setBonusBackgroundSecondLayer() {
    const bg = this.createSprite(this.resources[bonusBackgroundResourceName.backgroundSecondLayer]);
    this.container.addChild(bg);
  }

  setBonusCoins() {
    const texture = this.createSpine(this.resources[bonusBackgroundResourceName.bonusCoins]);
    this.setPosition(texture, gameConfig.backgroundAnimationPosition);
    texture.state.setAnimation(0, 'animation', true);
    this.container.addChild(texture);
  }

  setBonusLittleLight() {
    const texture = this.createSpine(this.resources[bonusBackgroundResourceName.bonusLittleLight]);
    this.setPosition(texture, gameConfig.backgroundAnimationPosition);
    texture.state.setAnimation(0, 'animation', true);
    this.container.addChild(texture);
  }

  setBonusPlants() {
    const texture = this.createSpine(this.resources[bonusBackgroundResourceName.bonusPlants]);
    this.setPosition(texture, gameConfig.backgroundAnimationPosition);
    texture.state.setAnimation(0, 'animation', true);
    this.container.addChild(texture);
  }

  setBonusStoneTreeLight() {
    const texture = this.createSpine(this.resources[bonusBackgroundResourceName.bonusStoneTreeLight]);
    this.setPosition(texture, gameConfig.backgroundAnimationPosition);
    texture.state.timeScale = 0.7;
    texture.state.setAnimation(0, 'moviong_light', true);
    this.container.addChild(texture);
  }

  setBonusStones() {
    const texture = this.createSpine(this.resources[bonusBackgroundResourceName.bonusStones]);
    this.setPosition(texture, gameConfig.backgroundAnimationPosition);
    this.__setAnimations(texture);
    texture.state.timeScale = 0.5;
    this.animations.glow();
    this.container.addChild(texture);
  }

  setBonusTopPlants() {
    const texture = this.createSpine(this.resources[bonusBackgroundResourceName.bonusTopPlants]);
    this.setPosition(texture, gameConfig.backgroundAnimationPosition);
    texture.state.setAnimation(0, 'animation', true);
    this.container.addChild(texture);
  }

  async onLoad() {
    const { res } = await super.loadResource([
      ...backgroundResource,
      ...preBonusBackgroundResources,
      ...bonusBackgroundResources,
    ], this.loader);
    this.resources = {
      ...this.resources,
      ...res,
    };
  }

  async loadMainResource() {
    if (!this.mainResource) {
      const { res } = await this.loadResource(backgroundResource, this.loader);
      this.mainResource = res;
      this.resources = {
        ...this.resources,
        ...res,
      };
    }
    return this.resources;
  }

  async loadPreBonusResource() {
    if (!this.preBonusresources) {
      const { res } = await this.loadResource(preBonusBackgroundResources, this.preBonusLoader);
      this.preBonusresources = res;
      this.resources = {
        ...this.resources,
        ...res,
      };
    }
    return this.preBonusresources;
  }

  async loadBonusResource() {
    if (!this.bonusresources) {
      const { res } = await this.loadResource(bonusBackgroundResources, this.bonusLoader);
      this.bonusresources = res;
      this.resources = {
        ...this.resources,
        ...res,
      };
    }
    return this.preBonusresources;
  }

  async executeMainScene() {
    await this.loadMainResource();
    this.setBackgroundFirstLayer();
    this.setPlanetsAnimation();
    this.setBackgroundSecondLayer();
    this.setTopPlanetsAnimation();
    this.setCoinsAnimations();
    this.setStoneAnimations();
    this.setLastResult();
  }

  async executePreBonusScene() {
    await this.loadPreBonusResource();
    this.setPreBonusBackgroundFirstLayer();
    this.setPreBonusPlants();
    this.setPreBonusBackgroundSecondLayer();
    this.setPreBonusMovingLiana();
    this.setPreBonusCoins();
    this.setPreBonusStones();
  }

  async executeBonusScene() {
    await this.loadBonusResource();
    this.setBonusBackgroundFirstLayer();
    this.setBonusPlants();
    this.setBonusStoneTreeLight();
    this.setBonusBackgroundSecondLayer();
    this.setPreBonusCoins();
    this.setBonusLittleLight();
    this.setBonusStones();
    this.setBonusTopPlants();
    this.setLastResult({
      lastResultObserver: LAST_RESULTS_BONUS,
    });
  }
}
